import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Client } from 'src/app/entities/client/client';
import { Person } from 'src/app/entities/person/person';
import { Project } from 'src/app/entities/project/project';
import { Tag } from 'src/app/entities/tag/tag';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { EntityInterface } from 'src/app/entities/interfaces/entity-interface';
import { User } from 'src/app/entities/user/user';

/**
 * Service to manage and store application data using BehaviorSubjects.
 * This allows components to subscribe to data changes and react accordingly.
 */
@Injectable({
  providedIn: 'root',
})
export class StoreService {

  /**
   * BehaviorSubject to hold the clients data.
   * @private
   */
  protected _clients$: BehaviorSubject<Client[]> = new BehaviorSubject<Client[]>([]);
  protected _persons$: BehaviorSubject<Person[]> = new BehaviorSubject<Person[]>([]);
  protected _projects$: BehaviorSubject<Project[]> = new BehaviorSubject<Project[]>([]);
  protected _tags$: BehaviorSubject<Tag[]> = new BehaviorSubject<Tag[]>([]);
  protected _time_tracks$: BehaviorSubject<TimeTrack[]> = new BehaviorSubject<TimeTrack[]>([]);
  protected _user$: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  protected _currentUser$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  protected _timerPlayerResume$ = new BehaviorSubject<TimeTrack | null>(null);

  /**
   * Saves the clients to the BehaviorSubject.
   * @param {Client[]} clients
   */
  public saveClients(clients: Client[]) {
    this._clients$.next(clients);
  }

  /**
   * Returns an Observable of the clients.
   * @returns {Observable<Client[]>}
   */
  get clients(): Observable<Client[]> {
    // Expose the data as an Observable for read-only access.
    return this._clients$.asObservable();
  }

  /**
   * Saves the persons to the BehaviorSubject.
   * @param {Person[]} persons
   */
  public savePersons(persons: Person[]) {
    this._persons$.next(persons);
  }

  /**
   * Returns an Observable of the persons.
   * @returns {Observable<Person[]>}
   */
  get persons(): Observable<Person[]> {
    return this._persons$.asObservable();
  }

  /**
   * Saves the projects to the BehaviorSubject.
   * @param {Project[]} projects
   */
  public saveProjects(projects: Project[]) {
    this._projects$.next(projects);
  }

  /**
   * Returns an Observable of the projects.
   * @returns {Observable<Project[]>}
   */
  get projects(): Observable<Project[]> {
    return this._projects$.asObservable();
  }

  /**
   * Saves the tags to the BehaviorSubject.
   * @param {Tag[]} tags
   */
  public saveTags(tags: Tag[]) {
    this._tags$.next(tags);
  }

  /**
   * Returns an Observable of the tags.
   * @returns {Observable<Tag[]>}
   */
  get tags(): Observable<Tag[]> {
    return this._tags$.asObservable();
  }

  /**
   * Saves the time tracks to the BehaviorSubject.
   * @param {TimeTrack[]} time_tracks
   */
  public saveTimeTracks(time_tracks: TimeTrack[]) {
    this._time_tracks$.next(time_tracks);
  }

  /**
   * Returns an Observable of the time tracks.
   * @returns {Observable<TimeTrack[]>}
   */
  get timeTracks(): Observable<TimeTrack[]> {
    return this._time_tracks$.asObservable();
  }

  /**
   * Saves the users to the BehaviorSubject.
   * @param {User[]} user
   */
  public saveUsers(user: User[]) {
    this._user$.next(user);
  }

  /**
   * Returns an Observable of the users.
   * @returns {Observable<User[]>}
   */
  get users(): Observable<User[]> {
    return this._user$.asObservable();
  }


  /**
   * Saves the current user to the BehaviorSubject.
   * @param {User} user
   */
  public saveCurrentUser(user: User) {
    this._currentUser$.next(user);
  }

  /**
   * Returns an Observable of the current user.
   * @returns {Observable<User>}
   */
  get currentUser(): Observable<User | null> {
    return this._currentUser$.asObservable();
  }


  /**
   * Saves the timerPlayerResume to the BehaviorSubject.
   * @param {TimeTrack} trackToResume
   */
  startTracking(trackToResume: TimeTrack | null): void {
    this._timerPlayerResume$.next(trackToResume);
  }


  /**
   * Returns an Observable of the timerPlayerResume.
   * @returns {Observable<TimeTrack>}
   */
  get timerPlayerResume(): Observable<TimeTrack | null> {
    return this._timerPlayerResume$.asObservable();
  }


  /**
   * Saves an array of entities to the appropriate BehaviorSubject based on the entity type.
   * @param entities An array of entities to save.
   */
  public saveEntities(entities: EntityInterface[], entityType?: string) {
    if (entities.length === 0 && entityType) {
      switch (entityType) {
        case 'TimeTrack':
          this.saveTimeTracks([]);
          break;
        case 'Project':
          this.saveProjects([]);
          break;
        case 'Tag':
          this.saveTags([]);
          break;
        case 'Client':
          this.saveClients([]);
          break;
        case 'User':
          this.saveUsers([]);
          break;
        case 'Person':
          this.savePersons([]);
          break;
      }
      return;
    }

    const entity = entities[0];
    switch (true) {
      case entity instanceof Client:
        this.saveClients(entities as Client[]);
        break;
      case entity instanceof Person:
        this.savePersons(entities as Person[]);
        break;
      case entity instanceof Project:
        this.saveProjects(entities as Project[]);
        break;
      case entity instanceof Tag:
        this.saveTags(entities as Tag[]);
        break;
      case entity instanceof TimeTrack:
        this.saveTimeTracks(entities as TimeTrack[]);
        break;
      case entity instanceof User:
        this.saveUsers(entities as User[]);
        break;
    }
  }

  /**
   * Updates a single entity in the store by merging it with the existing collection.
   * If the entity exists (by ID), it will be updated; otherwise, it will be added.
   * @param entity The entity to update or add
   */
  public updateEntity<T extends EntityInterface>(entity: T): void {
    if (entity instanceof Client) {
      this.updateSingleEntity(entity, this._clients$);
    } else if (entity instanceof Person) {
      this.updateSingleEntity(entity, this._persons$);
    } else if (entity instanceof Project) {
      this.updateSingleEntity(entity, this._projects$);
    } else if (entity instanceof Tag) {
      this.updateSingleEntity(entity, this._tags$);
    } else if (entity instanceof TimeTrack) {
      this.updateSingleEntity(entity, this._time_tracks$);
    } else if (entity instanceof User) {
      this.updateSingleEntity(entity, this._user$);
    }
  }

  /**
   * Adds a single entity to the store by adding it to the beginning of the collection.
   * @param entity The entity to add
   */
  public addEntity<T extends EntityInterface>(entity: T): void {
    if (entity instanceof Client) {
      this.addSingleEntity(entity, this._clients$);
    } else if (entity instanceof Person) {
      this.addSingleEntity(entity, this._persons$);
    } else if (entity instanceof Project) {
      this.addSingleEntity(entity, this._projects$);
    } else if (entity instanceof Tag) {
      this.addSingleEntity(entity, this._tags$);
    } else if (entity instanceof TimeTrack) {
      this.addSingleEntity(entity, this._time_tracks$);
    } else if (entity instanceof User) {
      this.addSingleEntity(entity, this._user$);
    }
  }

  /**
   * Helper method to update a single entity in a BehaviorSubject collection
   */
  private updateSingleEntity<T extends EntityInterface>(entity: T, subject: BehaviorSubject<T[]>): void {
    const currentEntities = [...subject.getValue()];
    const index = currentEntities.findIndex(e => e.id === entity.id);

    if (index !== -1) {
      currentEntities[index] = entity;
    } else {
      currentEntities.push(entity);
    }

    subject.next(currentEntities);
  }

  /**
   * Helper method to add a single entity to a BehaviorSubject collection
   */
  private addSingleEntity<T extends EntityInterface>(entity: T, subject: BehaviorSubject<T[]>): void {
    const currentEntities = [...subject.getValue()];
    currentEntities.unshift(entity);
    subject.next(currentEntities);
  }
}
